import { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Row, Col, Statistic, Spin, message, Tag } from 'antd';
import ReactEChartsCore from 'echarts-for-react';
import { digitalTwin } from '../../../services/api';

const CATEGORY_STYLE = {
  grid: { color: '#ff7875', symbol: 'rect' },
  park: { color: '#1890ff', symbol: 'roundRect' },
  pv: { color: '#faad14', symbol: 'circle' },
  storage: { color: '#52c41a', symbol: 'diamond' },
  charging: { color: '#722ed1', symbol: 'circle' },
  load: { color: '#8c8c8c', symbol: 'circle' },
};

export default function DigitalTwinPage() {
  const [loading, setLoading] = useState(true);
  const [twin, setTwin] = useState(null);

  const inFlightRef = useRef(false);

  const load = () => {
    // 轮询竞态守卫：上一轮未返回时跳过本轮，避免慢请求重叠乱序
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    digitalTwin.topology()
      .then((res) => setTwin(res.data))
      .catch((err) => message.error(err.message || '加载失败'))
      .finally(() => {
        inFlightRef.current = false;
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 8000);
    return () => clearInterval(timer);
  }, []);

  const graphOption = useMemo(() => {
    if (!twin?.nodes) return {};
    const categories = [...new Set(twin.nodes.map((n) => n.category))].map((c) => ({ name: c }));
    return {
      tooltip: {
        formatter: (p) => {
          if (p.dataType === 'edge') return `${p.data.source} → ${p.data.target}: ${p.data.value} kW`;
          const d = p.data;
          return `${d.name}<br/>功率: ${d.power_kw} kW${d.soc != null ? `<br/>SOC: ${d.soc}%` : ''}`;
        },
      },
      legend: { data: categories.map((c) => c.name), bottom: 0 },
      series: [{
        type: 'graph',
        layout: 'force',
        roam: true,
        draggable: true,
        categories,
        data: twin.nodes.map((n) => ({
          id: n.id,
          name: n.name,
          value: n.power_kw,
          symbolSize: n.symbolSize,
          category: categories.findIndex((c) => c.name === n.category),
          itemStyle: { color: CATEGORY_STYLE[n.category]?.color },
          label: { show: true, fontSize: 10 },
          ...n,
        })),
        edges: twin.edges.map((e) => ({
          ...e,
          lineStyle: { width: Math.max(1, Math.log10(e.value + 1) * 2), curveness: 0.15 },
        })),
        force: { repulsion: 300, edgeLength: [80, 160] },
        emphasis: { focus: 'adjacency' },
      }],
    };
  }, [twin]);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  const m = twin?.metrics || {};

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row align="middle">
          <Col flex="auto">
            <span style={{ fontWeight: 600, fontSize: 16 }}>数字孪生 · {twin?.space?.name}</span>
            <Tag color="green" style={{ marginLeft: 12 }}>同步 {twin?.sync_latency_ms}ms</Tag>
            <Tag>{twin?.twin_status}</Tag>
          </Col>
          <Col>
            {(twin?.data_sources || []).map((s) => <Tag key={s}>{s}</Tag>)}
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        <Col span={4}><Card size="small"><Statistic title="市电进线" value={m.grid_import_kw} suffix="kW" /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="光伏发电" value={m.pv_generation_kw} suffix="kW" valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="储能功率" value={m.storage_power_kw} suffix="kW" valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="充电负荷" value={m.charging_power_kw} suffix="kW" /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="总负荷" value={m.total_load_kw} suffix="kW" /></Card></Col>
        <Col span={4}><Card size="small"><Statistic title="自给率" value={m.self_sufficiency_pct} suffix="%" valueStyle={{ color: '#1890ff' }} /></Card></Col>
      </Row>

      <Card title="能源拓扑孪生体（实时能流）" style={{ marginTop: 16 }} size="small">
        <ReactEChartsCore option={graphOption} style={{ height: 480 }} />
      </Card>
    </div>
  );
}
